import React, {useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useRecoilState, useRecoilValue, useSetRecoilState} from 'recoil';
import {Capacitor} from '@capacitor/core';
import {Share} from '@capacitor/share';

import {makeLike} from '@/services/community';
import {
  CommentType,
  CommunityCommentCount,
  CommunityIsLike,
  CommunityLikeCount,
  ToastMessage,
  isOpenLoginModal,
  isOpenToast,
} from '@/store';
import {user} from '@/store/user';
import useNative from '@/hooks/useNative';
import useKakao from '@/hooks/useKakao';

import styles from '../../contents/ContentsFooter/styles.module.scss';

interface Props {
  id: number;
  contents: Community | undefined;
  setter: (c: Community) => void;
}

export default function CommunityFooter({
  id,
  contents,
  setter: setContents,
}: Props) {
  const navigator = useNavigate();
  const Echo = useNative();
  const kakao = useKakao();
  const userInfo = useRecoilValue(user);
  const [isOpenShare, setIsOpenShare] = useState<boolean>(false);
  const [userLike, setUserLike] = useRecoilState(CommunityIsLike);
  const likeCount = useRecoilValue(CommunityLikeCount);
  const commentCount = useRecoilValue(CommunityCommentCount);
  const setLoginModal = useSetRecoilState(isOpenLoginModal);
  const setCommentType = useSetRecoilState(CommentType);
  const setToastMessage = useSetRecoilState(ToastMessage);
  const setIsOpenToast = useSetRecoilState(isOpenToast);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const clickLike = async () => {
    if (!userInfo) {
      return setLoginModal(true);
    }
    const c = contents as Community;
    const next = !userLike;
    await makeLike(id, next, userInfo);
    setUserLike(next);
    setContents({
      ...c,
      isLike: next,
      likeCount: next ? likeCount + 1 : likeCount - 1,
    });
  };

  const moveToComment = async () => {
    await setCommentType('community');
    navigator(`/comments/${id}`);
  };

  const makeUrl = () => {
    const host =
      process.env.NODE_ENV !== 'development'
        ? 'https://knewnew.co.kr'
        : 'https://dev.knewnew.co.kr';
    return `${host}${window.location.pathname}`;
  };

  const copy = async () => {
    const {title} = contents as Community;
    if (Capacitor.getPlatform() === 'web') {
      window.navigator.clipboard.writeText(`${makeUrl()}`);
      setToastMessage('링크가 복사되었습니다.');
      setIsOpenToast(true);
    } else {
      await Share.share({
        title: `${title}`,
        text: `${title}`,
        url: `${makeUrl()}`,
      });
    }
    setIsOpenShare(false);
  };

  const shareKakao = async () => {
    const {title, content, images} = contents as Community;
    if (Capacitor.getPlatform() === 'web') {
      kakao.share({
        title,
        description: content.substring(0, 50),
        imageUrl: images[0].image,
        link: window.location.href,
      });
    } else {
      await Echo.echo({
        value: `share*${title}*${content.substring(0, 50)}*${
          images[0].image
        }*${makeUrl()}*${`${window.location.pathname}`}`,
      });
    }
    setIsOpenShare(false);
  };

  const handleClickOutside = (event: any) => {
    if (wrapperRef && !wrapperRef?.current?.contains(event.target)) {
      setIsOpenShare(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="community-footer">
      <div
        className={styles.share_items_left_panel}
        style={{display: isOpenShare ? 'block' : 'none'}}
        ref={wrapperRef}>
        <div className={styles.share_item} onClick={() => shareKakao()}>
          <span className={styles.kakao_icon}>&nbsp;</span>
          <span className={styles.txt}>카카오톡 공유</span>
        </div>
        <div className={styles.share_item} onClick={() => copy()}>
          <span className={styles.link_copy_icon}>&nbsp;</span>
          <span className={styles.txt}>링크 공유</span>
        </div>
      </div>
      <span
        className={styles.share_btn}
        onClick={() => setIsOpenShare(!isOpenShare)}>
        &nbsp;
      </span>
      <div className="like-share-comment">
        <div
          className="comment-wrapper"
          onClick={() => moveToComment()}
          style={{cursor: 'pointer'}}>
          <span className="comment-icon">&nbsp;</span>
          <span className="like-cnt">{commentCount}</span>
        </div>
        <div
          className="like-wrapper"
          style={{cursor: 'pointer'}}
          onClick={() => clickLike()}>
          <span
            className="icon like-icon"
            style={{display: !userLike ? 'inline-block' : 'none'}}>
            &nbsp;
          </span>
          <span
            className="icon reaction-best-large-icon active"
            style={{display: userLike ? 'inline-block' : 'none'}}>
            &nbsp;
          </span>
          <span className="like-cnt">{likeCount}</span>
        </div>
      </div>
    </div>
  );
}
